import { useEffect, useRef, useState } from "react";
import { prefersReducedMotion } from "../../../utils/reducedMotion";
import { DEEP_LINK_HIGHLIGHT_MS } from "../../UIToolkit/Animations/deepLinkWash";
import type { WageSettingsTarget } from "./deepLink";

/** What a section is told about a deep link: `undefined` when the link points at another section (or none). */
export type WageRuleDeepLink = WageSettingsTarget | undefined;

/**
 * Which of a section's rows a deep link points at, by index. `[]` = land on the section, highlight
 * nothing. Handed the whole target so it can narrow on `section` itself. Must be a stable reference
 * (module-level), since it is an effect dependency.
 */
export type DeepLinkRowMatcher<TRow> = (target: WageSettingsTarget, rows: readonly TRow[]) => number[];

const NO_ROWS: ReadonlySet<number> = new Set();

/**
 * Lands a Wage Settings deep link on one section: scrolls the section into view once its stored rows
 * are there (before that, the content below it is still empty and the page would shift under the
 * scroll), and highlights the rows `matchRows` picks for `DEEP_LINK_HIGHLIGHT_MS`.
 *
 * No match — the rule was renamed or deleted since the wage breakdown was computed, or the link names
 * no row — still scrolls to the section, which is the honest answer: "it lived here".
 */
export const useWageRuleDeepLink = <TRow>(target: WageRuleDeepLink, rowsLoaded: boolean, rows: readonly TRow[], matchRows: DeepLinkRowMatcher<TRow>) => {
	const sectionRef = useRef<HTMLElement>(null);
	const [highlightedRowIndices, setHighlightedRowIndices] = useState<ReadonlySet<number>>(NO_ROWS);
	// Per target object rather than a boolean, so a later link to the same mounted section lands too.
	const handledTarget = useRef<WageRuleDeepLink>(undefined);

	useEffect(() => {
		if (target === undefined || !rowsLoaded || handledTarget.current === target) return;
		handledTarget.current = target;
		sectionRef.current?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
		const indices = matchRows(target, rows);
		if (indices.length > 0) setHighlightedRowIndices(new Set(indices));
	}, [target, rowsLoaded, rows, matchRows]);

	useEffect(() => {
		if (highlightedRowIndices.size === 0) return;
		const timer = setTimeout(() => setHighlightedRowIndices(NO_ROWS), DEEP_LINK_HIGHLIGHT_MS);
		return () => clearTimeout(timer);
	}, [highlightedRowIndices]);

	return { sectionRef, highlightedRowIndices, isTarget: target !== undefined };
};
